import { isElevationCancelled } from "../access/elevation";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Download, Info, Loader2, Pencil, RotateCcw, ShieldAlert, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { toApiFailure } from "../../lib/errors";
import { notifyApiError, type ErrorMessages } from "../../lib/toast-error";
import { formatDate, formatDateTime } from "../../lib/format";
import { cn } from "../../lib/utils";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Textarea } from "../../components/ui/textarea";
import {
  dsgvoKeys,
  downloadDisclosure,
  useConfirmReviewItem,
  useCreateDeletionRequest,
  useDeletionJournal,
  useEditReviewItem,
  usePeopleSearch,
  usePersonOccurrences,
  useReplayDeletion,
  useResolveManualReview,
  useReviewItems,
  type DeletionDisposition,
  type DeletionJournalEntry,
  type DsgvoOccurrence,
  type DsgvoPerson,
  type DsgvoPersonOccurrences,
  type DsgvoReviewItem,
} from "./api";
import {
  DATA_OBJECT_LABEL_KEY,
  FIELD_LABEL_KEY,
  FREE_TEXT_BADGE_CLASS,
  JOURNAL_KIND_LABEL_KEY,
  REVIEW_ITEM_STATUS_LABEL_KEY,
  canCloseReview,
  canReplayEntry,
  isErasable,
  isOrderEnabled,
  isValidReviewNote,
  journalBadges,
  lastUpdatedAt,
  MIN_REVIEW_NOTE_CHARS,
  needsElevation,
  openReviews,
  scopeTotal,
} from "./journal";
import { canCreateDisclosure, canOrderDeletion } from "./permissions";

/** Debounce of the live person search (ms). */
const SEARCH_DEBOUNCE_MS = 300;

const DISPOSITION_LABEL_KEY: Record<DeletionDisposition, string> = {
  tombstone: "dsgvo.dialog.disposition.tombstone",
  hash_retain: "dsgvo.dialog.disposition.hash_retain",
  purge: "dsgvo.dialog.disposition.purge",
};

/**
 * Asked when the server refuses the erasure order for want of an elevated
 * session ([F-033]); resolves true once the session is elevated, and the
 * order is then sent again. Wired by the app shell to the elevation dialog.
 * Without it the dialog shows the server's message only.
 */
export type ElevateSession = () => Promise<boolean>;

function useDsgvoErrorNotifier(): (error: unknown) => void {
  const { t } = useTranslation();
  const errorMessages: ErrorMessages = {
    precondition: t("errors.precondition"),
    forbidden: t("errors.forbidden"),
    conflict: t("errors.conflict"),
    generic: t("errors.generic"),
  };
  return (error: unknown): void => notifyApiError(error, errorMessages);
}

/**
 * Tab "Anfragen verwalten" - the DSGVO-Auditor's five functions of [F-024]:
 * the person search and the occurrences grouped by data object (1), the
 * Auskunft im Auftrag (2), the erasure order (3), the manual review of free
 * texts (4) and the Loeschjournal of both kinds (5). Actions follow `/me.can`;
 * the server re-checks every route (ADR-API-09). No address is shown ([F-030]):
 * the search finds a person by one, the result names them.
 */
export function ManageRequestsTab({ onNeedsElevation }: { onNeedsElevation?: ElevateSession }): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const mayErase = canOrderDeletion(user);
  const mayDisclose = canCreateDisclosure(user);

  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => setQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const search = usePeopleSearch(query);
  const journal = useDeletionJournal();

  const [selected, setSelected] = useState<DsgvoPerson | null>(null);
  const [orderTarget, setOrderTarget] = useState<{ person: DsgvoPerson; occurrences: DsgvoPersonOccurrences } | null>(null);
  const [editTarget, setEditTarget] = useState<DsgvoReviewItem | null>(null);
  const [closeTarget, setCloseTarget] = useState<DeletionJournalEntry | null>(null);

  const entries = journal.data ?? [];
  const reviews = openReviews(entries);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {mayErase ? t("dsgvo.manage.roleActionHint") : t("dsgvo.manage.readOnlyHint")}
      </p>

      <Card data-testid="dsgvo_search">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">{t("dsgvo.manage.searchTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Input
              data-testid="dsgvo_search-input"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t("dsgvo.manage.searchPlaceholder")}
              aria-label={t("dsgvo.manage.searchTitle")}
            />
            <p className="text-xs text-muted-foreground">{t("dsgvo.manage.searchHint")}</p>
          </div>

          {query.length === 0 ? null : search.isError ? (
            <ErrorBanner onRetry={() => void search.refetch()} />
          ) : search.isLoading || !search.data ? (
            <div className="space-y-2">
              <div className="h-12 w-full animate-pulse rounded-md bg-muted" />
              <div className="h-12 w-full animate-pulse rounded-md bg-muted" />
            </div>
          ) : search.data.length === 0 ? (
            <p data-testid="dsgvo_search-empty" className="text-sm text-muted-foreground">
              {t("dsgvo.manage.searchEmpty")}
            </p>
          ) : (
            <ul data-testid="dsgvo_search-results" className="divide-y rounded-md border">
              {search.data.map((person) => (
                <li
                  key={person.id}
                  data-testid={`dsgvo_person-row_${person.id}`}
                  className={cn(
                    "flex flex-wrap items-center justify-between gap-2 p-3",
                    selected?.id === person.id && "bg-muted/50",
                  )}
                >
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{person.displayName}</span>
                      <PersonBadges person={person} />
                    </div>
                    {person.otherNames.length > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        {t("dsgvo.manage.otherNames", { names: person.otherNames.join(", ") })}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    data-testid={`dsgvo_person-open_${person.id}`}
                    variant="outline"
                    size="sm"
                    className="h-11 sm:h-8"
                    onClick={() => setSelected(person)}
                  >
                    {t("dsgvo.manage.showOccurrences")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {selected ? (
        <PersonPanel
          key={selected.id}
          person={selected}
          mayDisclose={mayDisclose}
          mayErase={mayErase}
          onOrder={(occurrences) => setOrderTarget({ person: selected, occurrences })}
          onClose={() => setSelected(null)}
        />
      ) : null}

      {/* ADR-C-014: the two-stage transparency promise, info-blue, non-alarmist. */}
      <div className="flex items-start gap-2 rounded-lg border border-sky-300 dark:border-sky-700 bg-sky-50 dark:bg-sky-950/40 px-4 py-3 text-sm text-sky-900 dark:text-sky-100">
        <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{t("dsgvo.manage.twoStagePromise")}</span>
      </div>

      {reviews.length > 0 ? (
        <ReviewSection
          reviews={reviews}
          mayWork={mayErase}
          onEdit={(item) => setEditTarget(item)}
          onClose={(entry) => setCloseTarget(entry)}
        />
      ) : null}

      <JournalSection journal={journal} entries={entries} mayReplay={mayErase} />

      {/* Keyed per target so form state resets between targets. */}
      <OrderDeletionDialog
        key={orderTarget?.person.id ?? "closed"}
        target={orderTarget}
        onClose={() => setOrderTarget(null)}
        onNeedsElevation={onNeedsElevation}
      />
      <EditReviewItemDialog key={editTarget?.id ?? "closed"} item={editTarget} onClose={() => setEditTarget(null)} />
      <ResolveReviewDialog key={closeTarget?.id ?? "closed"} target={closeTarget} onClose={() => setCloseTarget(null)} />
    </div>
  );
}

function PersonBadges({ person }: { person: DsgvoPerson }): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      {!person.hasAccount ? <Badge variant="outline">{t("dsgvo.manage.badgeNoAccount")}</Badge> : null}
      {person.state === "retired" && !person.anonymized ? (
        <Badge variant="outline">{t("dsgvo.manage.badgeRetired")}</Badge>
      ) : null}
      {person.state === "unconfirmed" ? <Badge variant="outline">{t("dsgvo.manage.badgeUnconfirmed")}</Badge> : null}
      {person.anonymized ? (
        <Badge
          variant="outline"
          className="border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/40 text-slate-700 dark:text-slate-300"
        >
          {t("dsgvo.manage.badgeAnonymized")}
        </Badge>
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------------------------
 * [F-024] functions 1 and 2: a person's occurrences and the Auskunft
 * ------------------------------------------------------------------------- */

function PersonPanel({
  person,
  mayDisclose,
  mayErase,
  onOrder,
  onClose,
}: {
  person: DsgvoPerson;
  mayDisclose: boolean;
  mayErase: boolean;
  onOrder: (occurrences: DsgvoPersonOccurrences) => void;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const occurrences = usePersonOccurrences(person.id);
  const [isDisclosing, setIsDisclosing] = useState(false);
  const [disclosureError, setDisclosureError] = useState<string | null>(null);

  const onDisclose = async (): Promise<void> => {
    setIsDisclosing(true);
    setDisclosureError(null);
    try {
      await downloadDisclosure(person.id);
      toast.success(t("dsgvo.person.disclosureDone"));
      void queryClient.invalidateQueries({ queryKey: dsgvoKeys.journal });
    } catch (err) {
      setDisclosureError(toApiFailure(err).problem?.detail ?? t("dsgvo.person.disclosureError"));
    } finally {
      setIsDisclosing(false);
    }
  };

  const data = occurrences.data;
  const current = data?.person ?? person;
  const erasable = isErasable(current, data);

  return (
    <Card data-testid="dsgvo_person">
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <CardTitle className="text-base">{t("dsgvo.person.title", { name: current.displayName })}</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <PersonBadges person={current} />
            </div>
          </div>
          <Button
            data-testid="dsgvo_person-close"
            variant="ghost"
            size="sm"
            className="h-11 sm:h-8"
            aria-label={t("dsgvo.person.close")}
            onClick={onClose}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </Button>
        </div>
        {data ? (
          <p data-testid="dsgvo_person-summary" className="text-sm text-muted-foreground">
            {t("dsgvo.person.summary", { total: data.total, freeText: data.freeTextTotal })}
          </p>
        ) : null}
        <div data-testid="dsgvo_person-actions" className="flex flex-wrap items-center gap-2">
          {mayDisclose ? (
            <Button
              data-testid="dsgvo_person-disclosure"
              className="h-11 sm:h-9"
              disabled={isDisclosing || !data}
              onClick={() => void onDisclose()}
            >
              {isDisclosing ? (
                <Loader2 aria-hidden="true" className="animate-spin" />
              ) : (
                <Download aria-hidden="true" />
              )}
              {isDisclosing ? t("dsgvo.person.disclosureRunning") : t("dsgvo.person.disclosure")}
            </Button>
          ) : null}
          {mayErase && data && erasable ? (
            <Button
              data-testid="dsgvo_person-erase"
              variant="outline"
              className="h-11 text-destructive-text hover:text-destructive-text sm:h-9"
              onClick={() => onOrder(data)}
            >
              <Trash2 aria-hidden="true" className="h-4 w-4" />
              {t("dsgvo.person.erase")}
            </Button>
          ) : null}
        </div>
        {data && !current.hasAccount ? (
          <p className="text-xs text-muted-foreground">{t("dsgvo.person.noAccountHint")}</p>
        ) : data && current.anonymized ? (
          <p className="text-xs text-muted-foreground">{t("dsgvo.person.anonymizedHint")}</p>
        ) : data && data.erasureJournalId !== null ? (
          <p data-testid="dsgvo_person-erasure-ordered" className="text-xs text-muted-foreground">
            {t("dsgvo.person.erasureOrdered")}
          </p>
        ) : null}
        {disclosureError ? (
          <p data-testid="dsgvo_person-disclosure-error" role="alert" className="text-sm text-destructive-text">
            {disclosureError}
          </p>
        ) : null}
      </CardHeader>
      <CardContent>
        {occurrences.isError ? (
          <ErrorBanner onRetry={() => void occurrences.refetch()} />
        ) : !data ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-10 w-full animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : data.groups.length === 0 ? (
          <p data-testid="dsgvo_person-empty" className="text-sm text-muted-foreground">
            {t("dsgvo.person.empty")}
          </p>
        ) : (
          <div data-testid="dsgvo_occurrence-groups" className="space-y-4">
            {data.groups.map((group) => (
              <section
                key={group.dataObject}
                data-testid={`dsgvo_occurrence-group_${group.dataObject}`}
                className="space-y-2"
              >
                <h3 className="flex flex-wrap items-baseline gap-2 text-sm font-medium">
                  <span>{t(DATA_OBJECT_LABEL_KEY[group.dataObject])}</span>
                  <span className="text-xs font-normal text-muted-foreground tabular-nums">
                    {group.count === 1
                      ? t("dsgvo.person.groupCountOne")
                      : t("dsgvo.person.groupCountMany", { count: group.count })}
                  </span>
                </h3>
                <ul className="divide-y rounded-md border">
                  {group.occurrences.map((occurrence) => (
                    <OccurrenceRow key={`${occurrence.objectId}.${occurrence.field}`} occurrence={occurrence} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function IncidentLink({ incidentId, incidentNumber }: { incidentId: string | null; incidentNumber: string | null }) {
  const { t } = useTranslation();
  if (!incidentId) return null;
  return (
    <Link to="/incidents/$id" params={{ id: incidentId }} className="text-xs underline underline-offset-2">
      {t("dsgvo.person.incident", { number: incidentNumber ?? "" })}
    </Link>
  );
}

function OccurrenceRow({ occurrence }: { occurrence: DsgvoOccurrence }): JSX.Element {
  const { t } = useTranslation();
  const key = `${occurrence.objectId}.${occurrence.field}`;
  return (
    <li data-testid={`dsgvo_occurrence_${key}`} className="space-y-1 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{t(FIELD_LABEL_KEY[occurrence.field])}</span>
        {occurrence.freeText ? (
          <Badge variant="outline" className={cn("border", FREE_TEXT_BADGE_CLASS)}>
            {t("dsgvo.person.freeText")}
          </Badge>
        ) : null}
        <IncidentLink incidentId={occurrence.incidentId} incidentNumber={occurrence.incidentNumber} />
        {occurrence.timestamp ? (
          <span className="text-xs text-muted-foreground">{formatDateTime(occurrence.timestamp)}</span>
        ) : null}
      </div>
      <p className="line-clamp-3 whitespace-pre-wrap break-words text-muted-foreground">
        {occurrence.content ?? t("dsgvo.person.noContent")}
      </p>
    </li>
  );
}

/* ---------------------------------------------------------------------------
 * [F-024] function 3: the erasure order (two-stage confirmation, [F-033])
 * ------------------------------------------------------------------------- */

function OrderDeletionDialog({
  target,
  onClose,
  onNeedsElevation,
}: {
  target: { person: DsgvoPerson; occurrences: DsgvoPersonOccurrences } | null;
  onClose: () => void;
  onNeedsElevation?: ElevateSession;
}): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateDeletionRequest();
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<{ detail: string; elevation: boolean } | null>(null);

  const submit = (): void => {
    if (!target) return;
    setError(null);
    create.mutate(target.person.id, {
      onSuccess: () => {
        onClose();
        toast.success(t("dsgvo.dialog.ordered"));
      },
      onError: (err) => {
        if (isElevationCancelled(err)) return;
        setError({
          detail: toApiFailure(err).problem?.detail ?? t("errors.generic"),
          elevation: needsElevation(err),
        });
      },
    });
  };

  const elevateAndRetry = async (): Promise<void> => {
    if (!onNeedsElevation) return;
    if (await onNeedsElevation()) submit();
  };

  const occurrences = target?.occurrences;

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent data-testid="dsgvo_order-dialog" className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("dsgvo.dialog.title")}</DialogTitle>
          <DialogDescription>{t("dsgvo.dialog.intro")}</DialogDescription>
        </DialogHeader>

        {target ? (
          <p className="text-sm">
            <span className="text-muted-foreground">{t("dsgvo.dialog.subject")}:</span>{" "}
            <span className="font-medium">{target.person.displayName}</span>
          </p>
        ) : null}

        {occurrences ? (
          <div className="space-y-4">
            <div className="rounded-md border">
              <Table data-testid="dsgvo_order-dispositions">
                <TableHeader>
                  <TableRow>
                    <TableHead className="h-8 text-xs">{t("dsgvo.dialog.colField")}</TableHead>
                    <TableHead className="h-8 text-xs">{t("dsgvo.dialog.colDisposition")}</TableHead>
                    <TableHead className="h-8 text-xs">{t("dsgvo.dialog.colHint")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {occurrences.dispositions.map((row) => (
                    <TableRow key={`${row.targetTable}.${row.targetColumn}`}>
                      <TableCell className="whitespace-nowrap font-mono text-xs">
                        {row.targetTable}.{row.targetColumn}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">{t(DISPOSITION_LABEL_KEY[row.disposition])}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.hint}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">{t("dsgvo.dialog.scopeTitle")}</p>
              <p data-testid="dsgvo_order-scope" className="text-sm text-muted-foreground">
                {t("dsgvo.dialog.scopeSummary", { total: occurrences.total, freeText: occurrences.freeTextTotal })}
              </p>
            </div>
          </div>
        ) : null}

        {error ? (
          <div
            data-testid="dsgvo_order-error"
            role="alert"
            className="space-y-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
          >
            <p className="flex items-start gap-2">
              {error.elevation ? <ShieldAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" /> : null}
              <span>{error.detail}</span>
            </p>
            {error.elevation && onNeedsElevation ? (
              <Button
                data-testid="dsgvo_order-elevate"
                variant="outline"
                size="sm"
                className="h-11 sm:h-8"
                onClick={() => void elevateAndRetry()}
              >
                {t("dsgvo.dialog.elevate")}
              </Button>
            ) : null}
            {!error.elevation ? <p className="text-xs text-muted-foreground">{t("dsgvo.dialog.failedHint")}</p> : null}
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <Checkbox
            id="dsgvo-order-confirmed"
            data-testid="dsgvo_order-confirm-check"
            checked={confirmed}
            onCheckedChange={(checked) => setConfirmed(checked === true)}
          />
          <Label htmlFor="dsgvo-order-confirmed" className="text-sm">
            {t("dsgvo.dialog.confirmCheckbox")}
          </Label>
        </div>

        <DialogFooter>
          <Button data-testid="dsgvo_order-cancel" variant="outline" className="h-11 sm:h-9" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            data-testid="dsgvo_order-submit"
            variant="destructive"
            className="h-11 sm:h-9"
            disabled={!isOrderEnabled(confirmed, occurrences !== undefined, create.isPending)}
            onClick={submit}
          >
            {create.isPending ? t("dsgvo.dialog.submitting") : t("dsgvo.dialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------------------------------
 * [F-024] function 4: the manual review
 * ------------------------------------------------------------------------- */

function ReviewSection({
  reviews,
  mayWork,
  onEdit,
  onClose,
}: {
  reviews: readonly DeletionJournalEntry[];
  mayWork: boolean;
  onEdit: (item: DsgvoReviewItem) => void;
  onClose: (entry: DeletionJournalEntry) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(reviews.length === 1 ? (reviews[0]?.id ?? null) : null);
  return (
    <Card data-testid="dsgvo_review">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{t("dsgvo.review.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{t("dsgvo.review.intro")}</p>
        <ul className="space-y-3">
          {reviews.map((entry) => {
            const open = expanded === entry.id;
            return (
              <li key={entry.id} data-testid={`dsgvo_review-entry_${entry.id}`} className="space-y-3 rounded-md border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0 space-y-0.5">
                    <p className="text-sm font-medium">
                      {t("dsgvo.review.entry", {
                        name: entry.subjectLabel ?? t("common.none"),
                        date: formatDate(entry.appliedAt ?? entry.requestedAt),
                      })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("dsgvo.review.progress", { open: entry.reviewItemsOpen, total: entry.reviewItemsTotal })}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      data-testid={`dsgvo_review-toggle_${entry.id}`}
                      variant="outline"
                      size="sm"
                      className="h-11 sm:h-8"
                      onClick={() => setExpanded(open ? null : entry.id)}
                    >
                      {open ? t("dsgvo.review.hide") : t("dsgvo.review.show")}
                    </Button>
                    {mayWork ? (
                      <Button
                        data-testid={`dsgvo_review-close_${entry.id}`}
                        size="sm"
                        className="h-11 sm:h-8"
                        disabled={!canCloseReview(entry)}
                        title={canCloseReview(entry) ? undefined : t("dsgvo.review.resolveBlocked")}
                        onClick={() => onClose(entry)}
                      >
                        {t("dsgvo.review.resolve")}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {open ? <ReviewItems journalId={entry.id} mayWork={mayWork} onEdit={onEdit} /> : null}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function ReviewItems({
  journalId,
  mayWork,
  onEdit,
}: {
  journalId: string;
  mayWork: boolean;
  onEdit: (item: DsgvoReviewItem) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const items = useReviewItems(journalId);
  const confirm = useConfirmReviewItem();
  const notifyError = useDsgvoErrorNotifier();

  if (items.isError) return <ErrorBanner onRetry={() => void items.refetch()} />;
  if (!items.data) {
    return <div className="h-16 w-full animate-pulse rounded bg-muted" />;
  }
  if (items.data.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("dsgvo.review.noItems")}</p>;
  }

  const onConfirm = (item: DsgvoReviewItem): void => {
    confirm.mutate(item.id, {
      onSuccess: () => toast.success(t("dsgvo.review.confirmed")),
      onError: (error) => notifyError(error),
    });
  };

  return (
    <ul data-testid={`dsgvo_review-items_${journalId}`} className="divide-y rounded-md border">
      {items.data.map((item) => (
        <li key={item.id} data-testid={`dsgvo_review-item_${item.id}`} className="space-y-2 p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">
              {t(DATA_OBJECT_LABEL_KEY[item.dataObject])} - {t(FIELD_LABEL_KEY[item.field])}
            </span>
            <IncidentLink incidentId={item.incidentId} incidentNumber={item.incidentNumber} />
            <Badge variant="outline" className={cn(item.status === "open" && FREE_TEXT_BADGE_CLASS)}>
              {t(REVIEW_ITEM_STATUS_LABEL_KEY[item.status])}
            </Badge>
            {item.status !== "open" && item.resolvedAt ? (
              <span className="text-xs text-muted-foreground">
                {t("dsgvo.review.resolvedBy", {
                  name: item.resolvedByLabel ?? t("common.none"),
                  date: formatDateTime(item.resolvedAt),
                })}
              </span>
            ) : null}
          </div>
          <p className="whitespace-pre-wrap break-words rounded bg-muted/50 px-2 py-1.5 text-muted-foreground">
            {item.content ?? t("dsgvo.person.noContent")}
          </p>
          {mayWork && item.status === "open" ? (
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid={`dsgvo_review-item-edit_${item.id}`}
                variant="outline"
                size="sm"
                className="h-11 sm:h-8"
                onClick={() => onEdit(item)}
              >
                <Pencil aria-hidden="true" className="h-4 w-4" />
                {t("dsgvo.review.edit")}
              </Button>
              <Button
                data-testid={`dsgvo_review-item-confirm_${item.id}`}
                variant="outline"
                size="sm"
                className="h-11 sm:h-8"
                disabled={confirm.isPending && confirm.variables === item.id}
                onClick={() => onConfirm(item)}
              >
                <Check aria-hidden="true" className="h-4 w-4" />
                {t("dsgvo.review.confirm")}
              </Button>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function EditReviewItemDialog({ item, onClose }: { item: DsgvoReviewItem | null; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const edit = useEditReviewItem();
  const [text, setText] = useState(item?.content ?? "");
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  const onSave = (): void => {
    if (!item) return;
    setErrorDetail(null);
    edit.mutate(
      { itemId: item.id, text },
      {
        onSuccess: () => {
          onClose();
          toast.success(t("dsgvo.review.saved"));
        },
        onError: (error) => setErrorDetail(toApiFailure(error).problem?.detail ?? t("errors.generic")),
      },
    );
  };

  return (
    <Dialog
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent data-testid="dsgvo_review-edit-dialog" className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("dsgvo.review.editTitle")}</DialogTitle>
          <DialogDescription>{t("dsgvo.review.editIntro")}</DialogDescription>
        </DialogHeader>
        {item ? (
          <p className="text-sm font-medium">
            {t(DATA_OBJECT_LABEL_KEY[item.dataObject])} - {t(FIELD_LABEL_KEY[item.field])}
          </p>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="dsgvo-review-edit-text">{t("dsgvo.review.editText")}</Label>
          <Textarea
            id="dsgvo-review-edit-text"
            data-testid="dsgvo_review-edit-text"
            rows={6}
            className={cn(item?.json && "font-mono text-xs")}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          {item?.json ? <p className="text-xs text-muted-foreground">{t("dsgvo.review.editJsonHint")}</p> : null}
          {item?.required ? <p className="text-xs text-muted-foreground">{t("dsgvo.review.editRequiredHint")}</p> : null}
        </div>
        {errorDetail ? (
          <p data-testid="dsgvo_review-edit-error" role="alert" className="text-sm text-destructive-text">
            {errorDetail}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" className="h-11 sm:h-9" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            data-testid="dsgvo_review-edit-save"
            className="h-11 sm:h-9"
            disabled={edit.isPending || (item?.required === true && text.trim().length === 0)}
            onClick={onSave}
          >
            {edit.isPending ? t("dsgvo.review.saving") : t("dsgvo.review.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResolveReviewDialog({
  target,
  onClose,
}: {
  target: DeletionJournalEntry | null;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const resolve = useResolveManualReview();
  const [note, setNote] = useState("");
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  const onConfirm = (): void => {
    if (!target || !isValidReviewNote(note)) return;
    setErrorDetail(null);
    resolve.mutate(
      { journalId: target.id, note: note.trim() },
      {
        onSuccess: () => {
          onClose();
          toast.success(t("dsgvo.review.resolved"));
        },
        onError: (error) => setErrorDetail(toApiFailure(error).problem?.detail ?? t("errors.generic")),
      },
    );
  };

  const noteChars = note.trim().length;

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent data-testid="dsgvo_review-close-dialog">
        <DialogHeader>
          <DialogTitle>{t("dsgvo.review.dialogTitle")}</DialogTitle>
          <DialogDescription>{t("dsgvo.review.dialogIntro")}</DialogDescription>
        </DialogHeader>
        {target ? <p className="text-sm font-medium">{target.subjectLabel ?? t("common.none")}</p> : null}
        <div className="space-y-1.5">
          <Label htmlFor="dsgvo-review-note">{t("dsgvo.review.note")}</Label>
          <Textarea
            id="dsgvo-review-note"
            data-testid="dsgvo_review-close-note"
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t("dsgvo.review.noteHint", { chars: noteChars })}</p>
        </div>
        {errorDetail ? (
          <p data-testid="dsgvo_review-close-error" role="alert" className="text-sm text-destructive-text">
            {errorDetail}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" className="h-11 sm:h-9" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            data-testid="dsgvo_review-close-submit"
            className="h-11 sm:h-9"
            disabled={resolve.isPending || noteChars < MIN_REVIEW_NOTE_CHARS}
            onClick={onConfirm}
          >
            {resolve.isPending ? t("dsgvo.review.resolving") : t("dsgvo.review.resolve")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------------------------------
 * [F-024] function 5: the Loeschjournal
 * ------------------------------------------------------------------------- */

function JournalSection({
  journal,
  entries,
  mayReplay,
}: {
  journal: ReturnType<typeof useDeletionJournal>;
  entries: readonly DeletionJournalEntry[];
  mayReplay: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const replay = useReplayDeletion();
  const notifyError = useDsgvoErrorNotifier();

  const onReplay = (entry: DeletionJournalEntry): void => {
    replay.mutate(entry.id, {
      onSuccess: () => toast.success(t("dsgvo.journal.replayDone")),
      onError: (error) => notifyError(error),
    });
  };

  return (
    <Card data-testid="dsgvo_journal">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{t("dsgvo.journal.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {journal.isError ? (
          <ErrorBanner onRetry={() => void journal.refetch()} />
        ) : journal.isLoading || !journal.data ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-9 w-full animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="rounded-lg border border-dashed px-6 py-10 text-center">
            <p data-testid="dsgvo_journal-empty" className="text-sm text-muted-foreground">
              {t("dsgvo.journal.empty")}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop: dense table. */}
            <div className="hidden rounded-lg border md:block">
              <Table data-testid="dsgvo_journal-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("dsgvo.journal.colKind")}</TableHead>
                    <TableHead>{t("dsgvo.journal.colSubject")}</TableHead>
                    <TableHead>{t("dsgvo.journal.colRequestedAt")}</TableHead>
                    <TableHead>{t("dsgvo.journal.colScope")}</TableHead>
                    <TableHead>{t("dsgvo.journal.colStatus")}</TableHead>
                    <TableHead>{t("dsgvo.journal.colUpdated")}</TableHead>
                    {mayReplay ? <TableHead className="w-32">{t("dsgvo.journal.colActions")}</TableHead> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id} data-testid={`dsgvo_journal-row_${entry.id}`}>
                      <TableCell>
                        <Badge variant="outline">{t(JOURNAL_KIND_LABEL_KEY[entry.kind])}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{entry.subjectLabel ?? t("common.none")}</div>
                        <div className="text-xs text-muted-foreground">
                          {t("dsgvo.journal.requestedBy", { name: entry.requestedByLabel ?? t("common.none") })}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDateTime(entry.requestedAt)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                        {t("dsgvo.journal.scope", { count: scopeTotal(entry) })}
                      </TableCell>
                      <TableCell>
                        <JournalBadges entry={entry} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDateTime(lastUpdatedAt(entry))}
                      </TableCell>
                      {mayReplay ? (
                        <TableCell>
                          {canReplayEntry(entry) ? (
                            <Button
                              data-testid={`dsgvo_journal-replay_${entry.id}`}
                              variant="outline"
                              size="sm"
                              disabled={replay.isPending && replay.variables === entry.id}
                              onClick={() => onReplay(entry)}
                            >
                              <ReplayLabel pending={replay.isPending && replay.variables === entry.id} />
                            </Button>
                          ) : null}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile (>=360px): card list instead of the table. */}
            <ul data-testid="dsgvo_journal-cards" className="space-y-2 md:hidden">
              {entries.map((entry) => (
                <li key={entry.id} data-testid={`dsgvo_journal-card_${entry.id}`} className="space-y-2 rounded-md border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{t(JOURNAL_KIND_LABEL_KEY[entry.kind])}</Badge>
                    <span className="text-sm font-medium">{entry.subjectLabel ?? t("common.none")}</span>
                  </div>
                  <JournalBadges entry={entry} />
                  <p className="text-xs text-muted-foreground">
                    {t("dsgvo.journal.colRequestedAt")}: {formatDateTime(entry.requestedAt)} -{" "}
                    {t("dsgvo.journal.requestedBy", { name: entry.requestedByLabel ?? t("common.none") })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("dsgvo.journal.colScope")}: {t("dsgvo.journal.scope", { count: scopeTotal(entry) })}
                  </p>
                  {mayReplay && canReplayEntry(entry) ? (
                    <Button
                      data-testid={`dsgvo_journal-card-replay_${entry.id}`}
                      variant="outline"
                      size="sm"
                      className="h-11 w-full"
                      disabled={replay.isPending && replay.variables === entry.id}
                      onClick={() => onReplay(entry)}
                    >
                      <ReplayLabel pending={replay.isPending && replay.variables === entry.id} />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ReplayLabel({ pending }: { pending: boolean }): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      {pending ? (
        <Loader2 aria-hidden="true" className="animate-spin" />
      ) : (
        <RotateCcw aria-hidden="true" className="h-4 w-4" />
      )}
      {pending ? t("dsgvo.journal.replaying") : t("dsgvo.journal.replay")}
    </>
  );
}

/** Status badge(s) of one journal row, the review's progress, and a failure's cause. */
function JournalBadges({ entry }: { entry: DeletionJournalEntry }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1.5">
        {journalBadges(entry).map((badge) => (
          <Badge key={badge.labelKey} variant="outline" className={cn("whitespace-nowrap border", badge.className)}>
            {badge.date ? t(badge.labelKey, { date: formatDate(badge.date) }) : t(badge.labelKey)}
          </Badge>
        ))}
      </div>
      {entry.kind === "erasure" && entry.manualReviewStatus === "open" ? (
        <p className="text-xs text-muted-foreground">
          {t("dsgvo.journal.reviewOpen", { open: entry.reviewItemsOpen, total: entry.reviewItemsTotal })}
        </p>
      ) : entry.kind === "erasure" && entry.manualReviewStatus === "resolved" ? (
        <p className="text-xs text-muted-foreground">{t("dsgvo.journal.reviewResolved")}</p>
      ) : null}
      {entry.status === "failed" && entry.lastError ? (
        <p className="text-xs text-destructive-text">{t("dsgvo.journal.lastError", { error: entry.lastError })}</p>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function ErrorBanner({ onRetry }: { onRetry: () => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
    >
      <span>{t("errors.load")}</span>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t("common.retry")}
      </Button>
    </div>
  );
}
